import React, { useEffect, useId, useRef, useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import type { Organization } from '@types';
import { dateDisplayPreferences, formatLocalDateTime } from '../utils/localDateTime';
import MsaPaymentCheckout from './MsaPaymentCheckout';

const steps = ['Fee', 'Review', 'Sign', 'Payment'];
const consentLabels: Record<string, string> = {
    parties: 'I confirm the named legal parties.', term: 'I accept the 12-month auto-renewing term and 60-day non-renewal notice.',
    trial: 'I acknowledge the existing free trial end date shown above.', annualFee: 'I accept the annual MSA fee shown.',
    serviceFees: 'I understand service requests may carry separate fees.', governingLaw: 'I accept the stated governing law and arbitration terms.',
    liability: 'I accept the limitation of liability terms.', dataProtection: 'I accept the data protection terms.',
    electronicSignature: 'I consent to electronic records and signatures.', authority: 'I confirm I have authority to bind the organization.',
};
const inputClass = 'mt-1 w-full rounded-xl border border-slate-300 px-4 py-3 text-sm';
const money = (amount: number, currency = 'USD') => new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount / 100);
const call = async (name: string, data: any = {}) => (await httpsCallable(getFunctions(undefined, 'us-central1'), name)(data)).data as any;
const MsaLoadingStatus = ({ children }: { children: React.ReactNode }) => <div role="status" aria-live="polite" aria-busy="true" className="my-4 flex items-center justify-center gap-3 rounded-xl bg-slate-50 px-5 py-8 text-sm text-slate-600"><span aria-hidden="true" className="h-8 w-8 shrink-0 animate-spin rounded-full border-4 border-blue-100 border-t-blue-600 motion-reduce:animate-none" /><p>{children}</p></div>;

const MsaConversionModal: React.FC<{ organization: Organization; email: string; onClose: () => void }> = ({ organization, email, onClose }) => {
    const dialog = useRef<HTMLDialogElement>(null);
    const frameTarget = `msa-payment-${useId().replace(/\W/g, '')}`;
    const [context, setContext] = useState<any>(null);
    const [step, setStep] = useState(0);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [previewUrl, setPreviewUrl] = useState('');
    const [previewBusy, setPreviewBusy] = useState(false);
    const [consents, setConsents] = useState<Record<string, boolean>>({});
    const [signature, setSignature] = useState({ signatoryFirstName: '', signatoryLastName: '', signatoryTitle: '', typedSignature: '' });
    const [complete, setComplete] = useState(false);

    useEffect(() => {
        dialog.current?.showModal();
        const overflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        let canceled = false;
        void call('getMsaConversion').then(result => {
            if (canceled)
                return;
            setContext(result);
            setStep(result.signed ? 3 : 0);
        }).catch(failure => { if (!canceled) setError(failure.message || 'MSA details could not load.'); });
        const completed = (event: Event) => {
            event.preventDefault();
            setComplete(true);
        };
        window.addEventListener('inteloptics:paddle-checkout-completed', completed);
        return () => {
            canceled = true;
            document.body.style.overflow = overflow;
            window.removeEventListener('inteloptics:paddle-checkout-completed', completed);
            window.Paddle?.Checkout?.close();
        };
    }, []);

    useEffect(() => {
        if (step !== 1 || !context)
            return;
        let canceled = false;
        let objectUrl = '';
        setPreviewUrl('');
        setError('');
        setPreviewBusy(true);
        void call('previewMsaDocument', { conversion: true, reviewHash: context.reviewHash, dateDisplay: dateDisplayPreferences() }).then(result => {
            if (canceled)
                return;
            if (result.url) {
                setPreviewUrl(result.url);
                return;
            }
            const binary = atob(result.pdfBase64 || '');
            const bytes = new Uint8Array(binary.length);
            for (let index = 0; index < binary.length; index++)
                bytes[index] = binary.charCodeAt(index);
            objectUrl = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
            setPreviewUrl(objectUrl);
        }).catch(failure => { if (!canceled) setError(failure.message || 'The MSA preview could not load.'); })
            .finally(() => { if (!canceled) setPreviewBusy(false); });
        return () => {
            canceled = true;
            if (objectUrl)
                URL.revokeObjectURL(objectUrl);
        };
    }, [step, context]);

    const monthly = context?.quote?.billingCycle === 'monthly';
    const displayedConsentLabels = { ...consentLabels,
        term: monthly ? 'I accept the month-to-month auto-renewing term.' : consentLabels.term,
        annualFee: monthly ? 'I accept the monthly MSA fee shown.' : consentLabels.annualFee,
    };
    const accepted = Object.keys(consentLabels).every(key => consents[key]);
    const advance = async () => {
        if (step === 3)
            return;
        setError('');
        if (step < 2) {
            setStep(step + 1);
            return;
        }
        setBusy(true);
        try {
            await call('signTrialMsa', { ...signature, consents, reviewHash: context.reviewHash, dateDisplay: dateDisplayPreferences() });
            setStep(3);
        } catch (failure: any) {
            setError(failure.message || 'The MSA could not be signed.');
        } finally {
            setBusy(false);
        }
    };

    return <dialog ref={dialog} aria-labelledby="msa-conversion-title" onCancel={event => { event.preventDefault(); if (!busy) onClose(); }} className="onboarding-theme m-auto max-h-[92vh] w-[calc(100%-1rem)] max-[359px]:w-full sm:w-[calc(100%-2rem)] max-w-4xl overflow-y-auto rounded-2xl border border-slate-200 bg-white p-5 shadow-2xl sm:p-8">
        <header className="flex items-start justify-between gap-4">
            <div><h2 id="msa-conversion-title" className="text-2xl font-extrabold">Sign MSA &amp; Set Up Payment</h2><p className="mt-2 text-sm text-slate-600">{context?.trialEndsAt ? `Free trial ends ${formatLocalDateTime(context.trialEndsAt)}. Signing does not extend your trial.` : 'Complete your MSA and set up payment to continue.'}</p></div>
            <button type="button" autoFocus disabled={busy} onClick={onClose} aria-label="Close MSA setup" className="rounded-lg px-3 py-2 text-slate-600 disabled:opacity-50">✕</button>
        </header>
        <ol className="my-6 grid grid-cols-4 gap-2">{steps.map((label, index) => <li key={label} aria-current={step === index ? 'step' : undefined} className={`border-b-4 pb-2 text-sm font-bold ${index <= step ? 'border-blue-500 text-blue-700' : 'border-slate-200 text-slate-500'}`}>{label}</li>)}</ol>
        {error && <p role="alert" className="mb-4 rounded-xl bg-red-50 p-4 text-sm text-red-700">{error}</p>}
        {!context && !error && <MsaLoadingStatus>Loading your MSA details…</MsaLoadingStatus>}
        {context && <form onSubmit={event => { event.preventDefault(); void advance(); }}>
            {step === 0 && <section className="space-y-4">
                <div className="rounded-xl bg-blue-50 p-5 text-blue-900"><p>{monthly ? 'Monthly MSA fee' : 'Annual MSA fee'}</p><p className="mt-1 text-3xl font-extrabold">{money(context.quote.total, context.quote.currencyCode)}</p><p className="mt-3 text-sm">{new Date(context.trialEndsAt).getTime() > Date.now() ? 'Your first charge is scheduled for trial end after payment setup.' : 'Your trial has ended. Payment is required to restore access.'}</p></div>
                {context.quote.items.map((item: any) => <div key={item.priceId} className="flex justify-between gap-4 border-b border-slate-200 py-3 text-sm"><span>{item.name}{item.quantity > 1 ? ` × ${item.quantity}` : ''}</span><strong>{money(Number(item.amount) * item.quantity, context.quote.currencyCode)}</strong></div>)}
            </section>}
            {step === 1 && <section className="space-y-4">
                <div className="rounded-xl bg-slate-50 p-4 text-sm"><p><strong>Organization:</strong> {context.data.legalName}</p><p><strong>Contact:</strong> {context.data.firstName} {context.data.lastName} · {context.data.workEmail}</p></div>
                <p className="text-sm font-bold">{context.document.title} · {context.document.version}</p>
                {previewBusy && <MsaLoadingStatus>Preparing your MSA preview…</MsaLoadingStatus>}
                {previewUrl && <iframe title="Master Service Agreement" src={`${previewUrl}#toolbar=0&navpanes=0&view=FitH`} className="h-[50vh] min-h-80 w-full rounded-xl border border-slate-300" />}
                {Object.entries(displayedConsentLabels).map(([key, label]) => <label key={key} className="flex gap-3 text-sm leading-relaxed"><input type="checkbox" checked={consents[key] === true} onChange={event => setConsents(previous => ({ ...previous, [key]: event.target.checked }))} /><span>{label}</span></label>)}
            </section>}
            {step === 2 && <section className="space-y-4">
                {context.testSignature && <p className="rounded-xl bg-amber-50 p-4 text-sm text-amber-900">Testing only. This signature is not legally binding.</p>}
                <p className="rounded-xl bg-blue-50 p-4 text-sm text-blue-900">Type your full legal name exactly as your electronic signature.</p>
                <div className="grid gap-4 sm:grid-cols-2">{([['signatoryFirstName', 'Signatory first name'], ['signatoryLastName', 'Signatory last name'], ['signatoryTitle', 'Signatory job title'], ['typedSignature', 'Typed signature']] as const).map(([key, label]) => <label key={key} className="text-sm font-bold">{label}<input required maxLength={key === 'typedSignature' ? 160 : key === 'signatoryTitle' ? 100 : 80} value={signature[key]} onChange={event => setSignature(previous => ({ ...previous, [key]: event.target.value }))} className={inputClass} /></label>)}</div>
                <p className="text-sm text-slate-600">Signing executes your MSA. Payment setup follows next.</p>
            </section>}
            {step === 3 && <section className="space-y-4">
                <p className="rounded-xl bg-emerald-50 p-4 text-sm text-emerald-800">{complete ? 'Payment setup completed. Your subscription status will update once confirmed.' : 'Your MSA is signed. Set up payment to continue after your free trial.'}</p>
                {!complete && <><MsaPaymentCheckout organization={organization} email={email} frameTarget={frameTarget} /><div className={`${frameTarget} -mx-5 sm:mx-0`} /></>}
                {complete && <button type="button" onClick={onClose} className="rounded-xl bg-blue-600 px-5 py-3 text-sm font-bold text-white">Done</button>}
            </section>}
            {step < 3 && <footer className="mt-6 flex justify-between border-t border-slate-200 pt-5">
                <button type="button" disabled={step === 0 || busy} onClick={() => { setError(''); setStep(step - 1); }} className="rounded-xl px-5 py-3 text-sm font-bold text-slate-600 disabled:opacity-30">Back</button>
                <button disabled={busy || (step === 1 && (!accepted || !previewUrl || previewBusy || Boolean(error)))} className="rounded-xl bg-blue-600 px-5 py-3 text-sm font-bold text-white disabled:opacity-50">{busy ? 'Signing…' : step === 2 ? 'Sign MSA' : 'Continue'}</button>
            </footer>}
        </form>}
    </dialog>;
};

export default MsaConversionModal;
